import type { RSAACallAction, RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import {
    CLICK_CREDIT_BUREAU_SLIDEOUT_BUTTON,
    CLOSE_CREDIT_BUREAU_SLIDEOUT,
    DEAL_SPD_FAILURE,
    DEAL_SPD_REQUEST,
    DEAL_SPD_SUCCESS,
    PII_CONSISTENCY_FAILURE,
    PII_CONSISTENCY_REQUEST,
    PII_CONSISTENCY_SUCCESS,
    RESEND_ID_VERIFICATION_REQUEST,
    RESEND_ID_VERIFICATION_SUCCESS,
    RESEND_ID_VERIFICATION_FAILURE,
    CLEAR_RESEND_ID_VERIFICATION
} from '../actionTypes/complianceActionTypes';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { BffApiUrlResult } from '@makemydeal/dr-shared-ui-utils';
import { apiConfigHelper, locHrefUtil, MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { selectedOfferSelectors } from '@makemydeal/dr-shared-store';
import { RSAA } from 'redux-api-middleware';
import { nanoid } from 'nanoid';

export const fetchDealSpdData = (state: StateTree): RSAACallAction => {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult: BffApiUrlResult = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, '');
    const dealXgId = selectedOfferSelectors.getSelectedOfferDealXgId(state);
    const endpoint = `${bffApiUrlResult.url}/dealspd/${dealXgId}`;
    const request: RSAACallRequest = {
        endpoint,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        types: [DEAL_SPD_REQUEST, DEAL_SPD_SUCCESS, DEAL_SPD_FAILURE]
    };
    return {
        [RSAA]: request
    } as RSAACallAction;
};

export const fetchPIIConsistencyData = (state: StateTree): RSAACallAction => {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult: BffApiUrlResult = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, '');
    const dealXgId = selectedOfferSelectors.getSelectedOfferDealXgId(state);
    const endpoint = `${bffApiUrlResult.url}/getDealPII/${dealXgId}`;
    const correlationId = nanoid();
    const request: RSAACallRequest = {
        endpoint,
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CoxAuto-Correlation-Id': correlationId
        },
        types: [
            PII_CONSISTENCY_REQUEST,
            {
                type: PII_CONSISTENCY_SUCCESS,
                meta: { dealXgId }
            },
            PII_CONSISTENCY_FAILURE
        ]
    };
    return {
        [RSAA]: request
    } as RSAACallAction;
};

export const dealSpdRequest = () => ({
    type: DEAL_SPD_REQUEST
});

export const dealSpdSuccess = (data: any) => ({
    type: DEAL_SPD_SUCCESS,
    payload: { data }
});

export const dealSpdFailure = (error: any) => ({
    type: DEAL_SPD_FAILURE,
    payload: { error }
});

export const creditBureauSlideoutButton = () => ({
    type: CLICK_CREDIT_BUREAU_SLIDEOUT_BUTTON
});

export const closeCreditBureauSlideout = () => ({
    type: CLOSE_CREDIT_BUREAU_SLIDEOUT
});

export const piiConsistencyRequest = () => ({
    type: PII_CONSISTENCY_REQUEST
});

export const piiConsistencySuccess = (data: any, dealXgId?: string) => ({
    type: PII_CONSISTENCY_SUCCESS,
    payload: data,
    meta: { dealXgId }
});

export const piiConsistencyFailure = (error: any) => ({
    type: PII_CONSISTENCY_FAILURE,
    payload: { error }
});

export const resendIdVerificationRequest = (dealId: string, phoneNumber: string): RSAACallAction => {
    const locHref = locHrefUtil.getLocHref();
    const bffApiUrlResult: BffApiUrlResult = apiConfigHelper.buildBffApiUrl(MANAGER_VIEW_APP_PREFIX, locHref, '');
    const endpoint = `${bffApiUrlResult.url}/postDealIDV/${dealId}`;
    const correlationId = nanoid();
    const request: RSAACallRequest = {
        endpoint,
        method: 'POST',
        body: JSON.stringify({ phoneNumber }),
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CoxAuto-Correlation-Id': correlationId
        },
        types: [
            RESEND_ID_VERIFICATION_REQUEST,
            {
                type: RESEND_ID_VERIFICATION_SUCCESS,
                meta: { dealId, correlationId }
            },
            RESEND_ID_VERIFICATION_FAILURE
        ]
    };
    return {
        [RSAA]: request
    } as RSAACallAction;
};

export const resendIdVerificationSuccess = (data: any, dealId?: string) => ({
    type: RESEND_ID_VERIFICATION_SUCCESS,
    payload: data,
    meta: { dealId }
});

export const resendIdVerificationFailure = (error: any) => ({
    type: RESEND_ID_VERIFICATION_FAILURE,
    payload: { error }
});

export const clearResendIdVerification = () => ({
    type: CLEAR_RESEND_ID_VERIFICATION
});
